import { useEffect, useRef } from "react"

import type { QueryBinding } from "../dashboard/catalog"

import { useDashboardStateStore } from "../state/dashboard-state"

/**
 * Pure helper — extracts the union of JSON-Pointer paths a binding
 * list references through its `params_from_state` maps. Exposed
 * separately so unit tests can verify the path-collection semantics
 * without spinning up a React renderer.
 */
export function collectWatchedFilterPaths(queryBindings: QueryBinding[] | undefined): string[] {
  if (!queryBindings || queryBindings.length === 0) return []
  const paths = new Set<string>()
  for (const binding of queryBindings) {
    const params = binding.params_from_state
    if (!params) continue
    for (const path of Object.values(params)) {
      if (typeof path === "string" && path.length > 0) paths.add(path)
    }
  }
  return Array.from(paths)
}

/**
 * Pure helper — given a baseline snapshot and the current values at
 * each watched path, returns true iff at least one path has a
 * different value (Object.is). Exposed alongside the hook so the
 * dedup/debounce contract is testable in isolation.
 */
export function detectFilterStateDirty(
  baseline: Map<string, unknown>,
  current: Map<string, unknown>
): boolean {
  for (const [path, currentValue] of current) {
    if (!Object.is(currentValue, baseline.get(path))) return true
  }
  return false
}

/**
 * Watch the dashboard state store for changes that affect SQL bindings.
 * When any state path referenced by a binding's `params_from_state`
 * map changes, fire `onRefresh` with a small debounce so a flurry of
 * mutations (e.g. multi-select toggle, range slider drag) collapses
 * into one network round-trip.
 *
 * This is the auto-refresh-on-filter-change wire that turns the
 * existing `Select` / `Checkbox` / `TextInput` widgets into live
 * filters — the same UX Power BI ships out of the box.
 *
 * No-ops when:
 *  - no binding declares `params_from_state` (nothing to watch)
 *  - the dashboard isn't persisted (refresh has no doc to write to)
 *  - the latest state was just sourced from the refresh response
 *    itself (would loop)
 *
 * Returns nothing — this is a side-effect hook. The caller wires
 * `onRefresh` to whatever fires the per-dashboard refresh endpoint.
 */
export function useAutoRefreshOnFilterChange({
  queryBindings,
  enabled,
  onRefresh,
  debounceMs = 350,
}: {
  queryBindings: QueryBinding[] | undefined
  enabled: boolean
  onRefresh: () => void | Promise<void>
  debounceMs?: number
}): void {
  const store = useDashboardStateStore()
  const lastSnapshotRef = useRef<Map<string, unknown>>(new Map())
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Hold the latest callback so the subscriber doesn't capture a stale
  // closure when the caller re-renders with a new function identity.
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  useEffect(() => {
    if (!enabled) return
    if (!queryBindings || queryBindings.length === 0) return

    // Build the set of JSON-Pointer paths we care about, deduped.
    const watchedPaths = new Set<string>()
    for (const binding of queryBindings) {
      const params = binding.params_from_state
      if (!params) continue
      for (const path of Object.values(params)) {
        if (typeof path === "string" && path.length > 0) {
          watchedPaths.add(path)
        }
      }
    }
    if (watchedPaths.size === 0) return

    // Prime the last-snapshot map so the *first* state mutation we see
    // is compared against the post-mount baseline, not an empty map
    // (which would always look "changed").
    lastSnapshotRef.current = new Map()
    for (const path of watchedPaths) {
      lastSnapshotRef.current.set(path, store.getDashState(path))
    }

    const unsubscribe = store.subscribe(() => {
      let dirty = false
      for (const path of watchedPaths) {
        const current = store.getDashState(path)
        const previous = lastSnapshotRef.current.get(path)
        if (!Object.is(current, previous)) {
          lastSnapshotRef.current.set(path, current)
          dirty = true
        }
      }
      if (!dirty) return

      if (timerRef.current !== null) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        Promise.resolve(onRefreshRef.current()).catch((err) => {
          // Mirror the [dash] prefix used by other runtime diagnostics
          // so ops can grep one keyword across the dashboard tree.

          console.error("[dash] auto-refresh failed:", err)
        })
      }, debounceMs)
    })

    return () => {
      unsubscribe()
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
    // The set of watched paths is derived from queryBindings —
    // recompute whenever the binding list changes (typical on dash
    // load or after the agent adds a new tile).
  }, [store, queryBindings, enabled, debounceMs])
}
